import React, { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { formatDateTime } from "@/lib/statuts";
import { Coins, Loader2, ShoppingCart, X, ArrowUpCircle, ArrowDownCircle, AlertTriangle, Bell, Save } from "lucide-react";
import { toast } from "sonner";

const DEFAULT_LOW = 5;

export default function MesJetons() {
  const { user } = useAuth();
  const eid = user.entreprise_id;
  const [solde, setSolde] = useState(null);
  const [movements, setMovements] = useState([]);
  const [tarifs, setTarifs] = useState([]);
  const [buyOpen, setBuyOpen] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(() => {
    if (!eid) { setErr("Aucune entreprise associée à votre compte."); return; }
    api.get(`/partenaires/${eid}/solde`).then((r) => setSolde(r.data)).catch((e) => setErr(apiError(e)));
    api.get(`/partenaires/${eid}/mouvements`).then((r) => setMovements(r.data)).catch(() => {});
    api.get("/payments/tarifs").then((r) => setTarifs(r.data)).catch(() => {});
  }, [eid]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (solde && (solde.soldes || []).some((b) => b.restant <= (solde.seuil_alerte_jetons ?? DEFAULT_LOW))) {
      toast.warning("Certains soldes de jetons sont bas. Pensez à recharger.");
    }
  }, [solde]);

  if (err) return <div className="rounded-xl border border-rose-200 bg-rose-50 p-6 text-rose-700" data-testid="jetons-error">{err}</div>;
  if (!solde) return <div className="flex h-96 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  if (solde.prepayment_mode !== "prepaiement") {
    return <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-slate-500" data-testid="jetons-non-prepaiement">Votre compte n'est pas en mode prépaiement. Contactez CVT pour activer l'achat de jetons.</div>;
  }

  const soldes = solde.soldes || [];
  const seuil = solde.seuil_alerte_jetons ?? DEFAULT_LOW;
  const lowItems = soldes.filter((b) => b.restant <= seuil);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900">Mes jetons</h1>
          <p className="text-sm text-slate-500">Achetez et suivez vos jetons de visite technique</p>
        </div>
        <button data-testid="buy-tokens-btn" onClick={() => setBuyOpen(true)} className="flex items-center gap-2 rounded-lg bg-violet-600 px-4 py-2 text-sm font-semibold text-white transition-colors duration-200 hover:bg-violet-700">
          <ShoppingCart className="h-4 w-4" /> Acheter des jetons
        </button>
      </div>

      {lowItems.length > 0 && (
        <div data-testid="low-balance-banner" className="flex flex-wrap items-center gap-3 rounded-xl border border-rose-200 bg-rose-50 p-4">
          <AlertTriangle className="h-5 w-5 shrink-0 text-rose-500" />
          <div className="flex-1 text-sm text-rose-700">
            <b>Solde bas :</b> {lowItems.map((b) => `${b.vt_type} (${b.restant})`).join(", ")}. Rechargez pour continuer à créer des visites.
          </div>
          <button data-testid="low-balance-buy" onClick={() => setBuyOpen(true)} className="rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-rose-700">Recharger</button>
        </div>
      )}

      {soldes.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-slate-400" data-testid="jetons-empty">Aucun jeton pour le moment. Cliquez sur « Acheter des jetons » pour commencer.</div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {soldes.map((b) => {
            const low = b.restant <= seuil;
            return (
              <div key={b.vt_type} data-testid={`jeton-card-${b.vt_type}`} className={`rounded-xl border bg-white p-5 ${low ? "border-rose-300" : "border-slate-200"}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Coins className={`h-4 w-4 ${low ? "text-rose-500" : "text-violet-500"}`} />
                    <span className="text-sm font-semibold text-slate-700">{b.vt_type}</span>
                  </div>
                  {low && <span className="flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-bold uppercase text-rose-600 animate-pulse"><AlertTriangle className="h-3 w-3" /> Bas</span>}
                </div>
                <div className="mt-3 font-heading text-3xl font-black text-slate-900">{b.restant}<span className="text-base font-medium text-slate-400"> / {b.quantite_totale}</span></div>
                <div className="text-xs text-slate-400">jetons restants</div>
              </div>
            );
          })}
        </div>
      )}

      <NotificationSettings eid={eid} solde={solde} onSaved={load} />

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="border-b border-slate-100 px-4 py-3 text-sm font-bold text-slate-700">Historique des mouvements</div>
        {movements.length === 0 ? (
          <p className="py-8 text-center text-sm text-slate-400">Aucun mouvement</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[560px] text-left text-sm" data-testid="jetons-history">
              <thead><tr className="border-b border-slate-200 bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                <th className="px-4 py-2">Date</th><th className="px-4 py-2">Type VT</th><th className="px-4 py-2">Mouvement</th><th className="px-4 py-2">Solde</th><th className="px-4 py-2">Réf.</th>
              </tr></thead>
              <tbody>
                {movements.map((m) => (
                  <tr key={m.id} className="border-b border-slate-100">
                    <td className="px-4 py-2 text-xs text-slate-500">{formatDateTime(m.date)}</td>
                    <td className="px-4 py-2 text-xs text-slate-700">{m.vt_type}</td>
                    <td className="px-4 py-2">
                      <span className={`flex items-center gap-1 text-xs font-semibold ${m.delta > 0 ? "text-emerald-600" : "text-amber-600"}`}>
                        {m.delta > 0 ? <ArrowUpCircle className="h-3.5 w-3.5" /> : <ArrowDownCircle className="h-3.5 w-3.5" />}
                        {m.delta > 0 ? `+${m.delta}` : m.delta}
                      </span>
                    </td>
                    <td className="px-4 py-2 mono text-xs font-semibold text-violet-700">{m.solde_apres}</td>
                    <td className="px-4 py-2 text-xs text-slate-400">{m.reference}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {buyOpen && <BuyModal tarifs={tarifs} onClose={() => setBuyOpen(false)} />}
    </div>
  );
}

function NotificationSettings({ eid, solde, onSaved }) {
  const [email, setEmail] = useState(solde.email_contact || "");
  const [seuil, setSeuil] = useState(solde.seuil_alerte_jetons ?? 5);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/partenaires/${eid}/parametres`, { email_contact: email, seuil_alerte_jetons: Number(seuil) });
      toast.success("Paramètres enregistrés");
      onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="notif-settings">
      <div className="mb-3 flex items-center gap-2">
        <Bell className="h-4 w-4 text-violet-500" />
        <h3 className="text-sm font-bold text-slate-700">Reçus & alertes</h3>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">Email de contact (reçu d'achat)</label>
          <input data-testid="notif-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="contact@monentreprise.fr" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">Alerte quand il reste moins de</label>
          <div className="flex items-center gap-2">
            <input data-testid="notif-seuil" type="number" min="0" value={seuil} onChange={(e) => setSeuil(e.target.value)} className="w-24 rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            <span className="text-sm text-slate-500">jeton(s)</span>
          </div>
        </div>
      </div>
      <p className="mt-2 text-[11px] text-slate-400">Un reçu vous est envoyé à chaque achat. Sous le seuil, vous recevez une alerte pour recharger — aucun prélèvement automatique.</p>
      <button data-testid="notif-save" onClick={save} disabled={saving} className="mt-3 flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Enregistrer
      </button>
    </div>
  );
}

function BuyModal({ tarifs, onClose }) {
  const [vt, setVt] = useState(tarifs[0]?.nom || "");
  const [qte, setQte] = useState(10);
  const [loading, setLoading] = useState(false);
  const tarif = tarifs.find((t) => t.nom === vt);
  const total = tarif ? tarif.prix * Number(qte || 0) : 0;

  const pay = async () => {
    if (!vt || qte < 1) { toast.error("Sélectionnez un type et une quantité"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", { vt_type: vt, quantite: Number(qte), origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiError(e)); setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative z-10 w-full max-w-md rounded-2xl bg-white p-6 shadow-xl" data-testid="buy-modal">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-heading text-lg font-bold">Acheter des jetons</h2>
          <button onClick={onClose}><X className="h-5 w-5 text-slate-400" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">Type de VT</label>
            <select data-testid="buy-vt" value={vt} onChange={(e) => setVt(e.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm">
              <option value="">Sélectionner…</option>
              {tarifs.map((t) => <option key={t.nom} value={t.nom}>{t.nom} — {t.prix} €/jeton</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">Nombre de jetons</label>
            <input data-testid="buy-qte" type="number" min="1" value={qte} onChange={(e) => setQte(e.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
          </div>
          <div className="flex items-center justify-between rounded-lg bg-violet-50 p-3">
            <span className="text-sm text-violet-700">Total à payer</span>
            <span data-testid="buy-total" className="font-heading text-xl font-black text-violet-700">{total.toLocaleString("fr-FR")} €</span>
          </div>
        </div>
        <button data-testid="buy-pay" disabled={loading || !total} onClick={pay} className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-60">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShoppingCart className="h-4 w-4" />} Payer par carte
        </button>
        <p className="mt-2 text-center text-[11px] text-slate-400">Paiement sécurisé via Stripe</p>
      </div>
    </div>
  );
}
